import { topListQueryOptions } from "@/lib/productPageQuery";
import { useParams } from "@/lib/router-compat";
import { jsonLd } from "@/lib/seoHelpers";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import PageLoading from "@/components/PageLoading";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink, Star, Award, ChevronRight } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

const BASE_URL = "https://www.garden-review.co.uk";

interface TopListItem {
  product_id: string;
  position: number;
  custom_title?: string;
  write_up?: string;
  pick_label?: string;
}

const productTypeConfig: Record<string, { table: string; slugPrefix: string; label: string }> = {
  lawn_mowers: { table: "lawn_mowers", slugPrefix: "/lawn-mowers", label: "Lawn Mowers" },
  generators: { table: "generators", slugPrefix: "/generators", label: "Generators" },
  chainsaws: { table: "chainsaws", slugPrefix: "/chainsaws", label: "Chainsaws" },
  pressure_washers: { table: "pressure_washers", slugPrefix: "/pressure-washers", label: "Pressure Washers" },
};

export default function TopList() {
  const { slug } = useParams<{ slug: string }>();

  const { data: topList } = useSuspenseQuery(topListQueryOptions(slug || ""));

  const items: TopListItem[] = topList ? (topList.items as unknown as TopListItem[]) || [] : [];
  const config = topList ? productTypeConfig[topList.product_type] : null;

  const { data: products } = useQuery({
    queryKey: ["top-list-products", topList?.id, topList?.product_type],
    queryFn: async () => {
      if (!topList || items.length === 0 || !config) return [];
      const ids = items.map(i => i.product_id);
      const { data, error } = await supabase
        .from(config.table as any)
        .select("*")
        .in("id", ids) as { data: any[] | null; error: any };
      if (error) throw error;
      return data || [];
    },
    enabled: !!topList && items.length > 0,
  });

  // Also fetch affiliate entries
  const affiliateTable = topList ? `${topList.product_type === "lawn_mowers" ? "lawn_mower" : topList.product_type === "pressure_washers" ? "pressure_washer" : topList.product_type === "generators" ? "generator" : "chainsaw"}_affiliates` : null;
  const affiliateFK = topList ? (topList.product_type === "lawn_mowers" ? "mower_id" : topList.product_type === "pressure_washers" ? "washer_id" : topList.product_type === "generators" ? "generator_id" : "chainsaw_id") : null;

  const { data: affiliates } = useQuery({
    queryKey: ["top-list-affiliates", topList?.id],
    queryFn: async () => {
      if (!topList || items.length === 0 || !affiliateTable || !affiliateFK) return [];
      const ids = items.map(i => i.product_id);
      const { data, error } = await supabase
        .from(affiliateTable as any)
        .select("*")
        .in(affiliateFK, ids)
        .order("price_gbp", { ascending: true }) as { data: any[] | null; error: any };
      if (error) throw error;
      return data || [];
    },
    enabled: !!topList && items.length > 0,
  });

  if (!topList) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
            <p className="text-muted-foreground">This list doesn't exist or isn't published yet.</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const productMap = new Map<string, any>();
  products?.forEach(p => productMap.set(p.id, p));

  // Derive a dominant brand from the list (>=50% of products share a brand).
  // Used to render a "Browse all {Brand} {Category}" internal link near the end of the page.
  const brandCategorySlug: Record<string, string> = {
    lawn_mowers: "lawnmowers",
    pressure_washers: "pressure-washers",
    chainsaws: "chainsaws",
    generators: "generators",
  };
  let dominantBrand: { name: string; slug: string; categorySlug: string } | null = null;
  if (topList && products && products.length > 0) {
    const counts = new Map<string, number>();
    products.forEach((p: any) => {
      if (p.brand) counts.set(p.brand, (counts.get(p.brand) || 0) + 1);
    });
    let topBrand = "";
    let topCount = 0;
    counts.forEach((c, b) => { if (c > topCount) { topCount = c; topBrand = b; } });
    if (topBrand && topCount / products.length >= 0.5 && brandCategorySlug[topList.product_type]) {
      dominantBrand = {
        name: topBrand,
        slug: topBrand.toLowerCase().replace(/\s+/g, "-"),
        categorySlug: brandCategorySlug[topList.product_type],
      };
    }
  }

  const affiliateMap = new Map<string, any[]>();
  affiliates?.forEach(a => {
    const key = (a as any)[affiliateFK!];
    if (!affiliateMap.has(key)) affiliateMap.set(key, []);
    affiliateMap.get(key)!.push(a);
  });

  const sortedItems = [...items].sort((a, b) => a.position - b.position);
  const pageUrl = `${BASE_URL}/best/${slug}`;

  // JSON-LD schemas
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": topList.title,
    "description": topList.meta_description || topList.title,
    "numberOfItems": sortedItems.length,
    "itemListElement": sortedItems.map((item, idx) => {
      const product = productMap.get(item.product_id);
      return {
        "@type": "ListItem",
        "position": idx + 1,
        "name": item.custom_title || (product ? `${product.brand} ${product.model}` : `Product #${idx + 1}`),
        ...(product?.slug && config ? { "url": `${BASE_URL}${config.slugPrefix}/${product.slug}` } : {}),
      };
    }),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": "Reviews", "item": `${BASE_URL}/category/reviews` },
      { "@type": "ListItem", "position": 3, "name": topList.title },
    ],
  };

  return (
    <>
      <Helmet>
        {/* canonical is emitted server-side by the route head() */}
        <script type="application/ld+json">{jsonLd(itemListSchema)}</script>
        <script type="application/ld+json">{jsonLd(breadcrumbSchema)}</script>
      </Helmet>

      <Header />

      <main className="min-h-screen">
        {/* Breadcrumb */}
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/category/reviews" className="hover:text-foreground">Reviews</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-foreground font-medium truncate">{topList.title}</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-linear-to-br from-primary/5 to-primary/10 py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <Badge className="mb-4" variant="secondary">
              {config?.label || "Reviews"} Guide
            </Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
              {topList.title}
            </h1>
            {topList.meta_description && (
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {topList.meta_description}
              </p>
            )}
            {topList.featured_image && (
              <img
                src={topList.featured_image}
                alt={topList.featured_image_alt || topList.title}
                className="w-full max-w-2xl mx-auto rounded-xl mt-8 shadow-lg object-cover"
                loading="eager"
              />
            )}
            <div className="flex items-center justify-center gap-4 mt-6 text-sm text-muted-foreground">
              <span>By {topList.author || "Garden-Review"}</span>
              {topList.published_at && (
                <span>• Updated {new Date(topList.published_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
              )}
              <span>• {sortedItems.length} products reviewed</span>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 max-w-4xl py-8 md:py-12">
          {/* Introduction */}
          {topList.intro_content && (
            <div
              className="prose prose-lg max-w-none mb-12 text-foreground"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(topList.intro_content) }}
            />
          )}

          {/* Quick Jump */}
          <nav className="bg-muted/30 rounded-xl p-6 mb-12">
            <h2 className="text-lg font-semibold mb-3">Quick Navigation</h2>
            <ol className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sortedItems.map((item, idx) => {
                const product = productMap.get(item.product_id);
                return (
                  <li key={item.product_id}>
                    <a
                      href={`#pick-${idx + 1}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <span className="font-bold text-primary">#{idx + 1}</span>
                      <span>{item.custom_title || (product ? `${product.brand} ${product.model}` : `Product ${idx + 1}`)}</span>
                      {item.pick_label && (
                        <Badge variant="outline" className="text-xs ml-auto">{item.pick_label}</Badge>
                      )}
                    </a>
                  </li>
                );
              })}
            </ol>
          </nav>

          {/* Product Entries */}
          <div className="space-y-16">
            {sortedItems.map((item, idx) => {
              const product = productMap.get(item.product_id);
              if (!product) return null;

              const productAffiliates = affiliateMap.get(item.product_id) || [];
              const primaryLink = product.affiliate_link;
              const displayTitle = item.custom_title || `${product.brand} ${product.model}`;

              return (
                <article key={item.product_id} id={`pick-${idx + 1}`} className="scroll-mt-24">
                  {/* Pick header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold text-lg">
                      {idx + 1}
                    </div>
                    {item.pick_label && (
                      <Badge className="bg-primary/10 text-primary border-primary/20">
                        <Award className="h-3 w-3 mr-1" />
                        {item.pick_label}
                      </Badge>
                    )}
                  </div>

                  <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                    {displayTitle}
                  </h2>

                  {/* Product card */}
                  <div className="bg-card rounded-xl border overflow-hidden mb-6">
                    <div className="grid md:grid-cols-3 gap-6 p-6">
                      {/* Image */}
                      <div className="flex items-center justify-center bg-muted/20 rounded-lg p-4">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={displayTitle}
                            className="max-h-48 object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-48 flex items-center justify-center text-muted-foreground">
                            No image
                          </div>
                        )}
                      </div>

                      {/* Key specs */}
                      <div className="md:col-span-2 space-y-4">
                        <div className="flex items-center gap-3">
                          {product.rating && (
                            <div className="flex items-center gap-1">
                              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                              <span className="font-bold text-lg">{product.rating}</span>
                              <span className="text-muted-foreground">/5</span>
                            </div>
                          )}
                          {product.price_gbp && (
                            <span className="text-2xl font-bold text-primary">£{product.price_gbp}</span>
                          )}
                        </div>

                        {product.best_for && (
                          <p className="text-muted-foreground italic">Best for: {product.best_for}</p>
                        )}

                        {/* Pros & Cons */}
                        <div className="grid grid-cols-2 gap-4">
                          {product.pros?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-green-600 mb-1">Pros</h4>
                              <ul className="text-sm space-y-1">
                                {product.pros.slice(0, 3).map((pro: string, i: number) => (
                                  <li key={i} className="flex items-start gap-1">
                                    <span className="text-green-500 mt-0.5">✓</span>
                                    <span>{pro}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {product.cons?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-red-600 mb-1">Cons</h4>
                              <ul className="text-sm space-y-1">
                                {product.cons.slice(0, 3).map((con: string, i: number) => (
                                  <li key={i} className="flex items-start gap-1">
                                    <span className="text-red-500 mt-0.5">✗</span>
                                    <span>{con}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        {/* CTA buttons */}
                        <div className="flex flex-wrap gap-2 pt-2">
                          {primaryLink && (
                            <a href={primaryLink} target="_blank" rel="sponsored noopener noreferrer nofollow">
                              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                                View Deal
                                <ExternalLink className="ml-2 h-4 w-4" />
                              </Button>
                            </a>
                          )}
                          {product.slug && config && (
                            <Link to={`${config.slugPrefix}/${product.slug}`}>
                              <Button variant="outline">Full Review</Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Retailer prices */}
                    {productAffiliates.length > 0 && (
                      <div className="border-t px-6 py-4 bg-muted/20">
                        <h4 className="text-sm font-semibold mb-2">Compare Prices</h4>
                        <div className="flex flex-wrap gap-3">
                          {productAffiliates.map((aff: any) => (
                            <a
                              key={aff.id}
                              href={aff.affiliate_link}
                              target="_blank"
                              rel="sponsored noopener noreferrer nofollow"
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-sm"
                            >
                              <span className="font-medium">{aff.retailer_name}</span>
                              {aff.price_gbp && <span className="font-bold text-primary">£{aff.price_gbp}</span>}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Editorial write-up */}
                  {item.write_up && (
                    <div
                      className="prose max-w-none text-foreground"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.write_up) }}
                    />
                  )}
                </article>
              );
            })}
          </div>

          {/* Conclusion */}
          {topList.conclusion_content && (
            <div className="mt-16 pt-8 border-t">
              <h2 className="text-2xl font-bold mb-4">Our Verdict</h2>
              <div
                className="prose prose-lg max-w-none text-foreground"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(topList.conclusion_content) }}
              />
            </div>
          )}

          {/* Brand cross-link — sends authority to the brand+category landing page */}
          {dominantBrand && (
            <div className="mt-8">
              <Link
                to={`/brand/${dominantBrand.slug}/${dominantBrand.categorySlug}`}
                className="block group rounded-lg border-2 border-primary/30 bg-primary/5 p-5 hover:border-primary hover:shadow-md transition-all"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">More from this brand</p>
                <h3 className="text-lg md:text-xl font-bold text-foreground group-hover:text-primary transition-colors flex items-center justify-between gap-3">
                  <span>Browse all {dominantBrand.name} {config?.label || ""} reviews</span>
                  <ChevronRight className="h-5 w-5 shrink-0" />
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Every {dominantBrand.name} {(config?.label || "").toLowerCase()} we've independently reviewed in the UK — ratings, specs and prices in one place.
                </p>
              </Link>
            </div>
          )}

          {/* Affiliate disclosure */}
          <div className="mt-12 p-4 bg-muted/30 rounded-lg text-xs text-muted-foreground">
            <strong>Affiliate Disclosure:</strong> As an Amazon Associate and affiliate partner, we earn from qualifying purchases. 
            This means if you click on a link and make a purchase, we may receive a small commission at no extra cost to you.
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}
